import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Coins, TrendUp, TrendDown, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TYPE_LABELS = {
  credit_earned: "Earned", credit_reserved: "Reserved", credit_refund: "Refund",
  monthly_allocation: "Monthly allocation", task_posted: "Task posted",
  credit_released: "Released", credit_bonus: "Bonus", credit_adjustment: "Adjustment",
  impact_awarded: "Impact",
};

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [deptWallets, setDeptWallets] = useState([]);
  const [depts, setDepts] = useState({});

  useEffect(() => {
    (async () => {
      const [w, l, dw, d] = await Promise.all([
        api.get("/wallet/me"), api.get("/wallet/me/ledger"),
        api.get("/wallet/departments"), api.get("/departments"),
      ]);
      setWallet(w.data); setLedger(l.data); setDeptWallets(dw.data);
      setDepts(Object.fromEntries(d.data.map((dd) => [dd.dept_id, dd.name])));
    })();
  }, []);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="wallet-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Economy</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mt-1">Wallet & Credits</h1>
        <p className="text-sm text-zinc-500 mt-1">Credits represent economic value. Impact Points represent recognition.</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <BigStat label="Credits balance" value={wallet?.balance ?? 0} accent="#E1252A" icon={Coins} testid="wallet-balance" />
        <BigStat label="Lifetime earned" value={wallet?.lifetime_earned ?? 0} icon={TrendUp} testid="wallet-lifetime" />
        <BigStat label="Impact points" value={wallet?.impact_points ?? 0} icon={Sparkle} testid="wallet-impact" />
        <BigStat label="Earned this month" value={wallet?.monthly_earned ?? 0} icon={TrendUp} testid="wallet-monthly" />
      </div>

      <Tabs defaultValue="ledger" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="ledger" data-testid="tab-ledger">My ledger</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Department wallets</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="pt-4">
          <div className="border border-border bg-background">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 px-3">When</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Task</th>
                  <th className="text-right py-2 px-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((tx) => (
                  <tr key={tx.tx_id} className="border-b border-border hover:bg-zinc-50 dark:hover:bg-zinc-900">
                    <td className="py-2 px-3 text-zinc-500 text-xs">{new Date(tx.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">{TYPE_LABELS[tx.type] || tx.type}</td>
                    <td className="py-2 px-3 truncate max-w-[240px]">
                      {tx.task_id ? <Link to={`/tasks/${tx.task_id}`} className="hover:underline text-zinc-700 dark:text-zinc-300">{tx.task_id}</Link> : "—"}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${tx.to_wallet?.includes(`user:${user.user_id}`) ? "text-emerald-600" : "text-zinc-700 dark:text-zinc-300"}`}>
                      {tx.to_wallet?.includes(`user:${user.user_id}`) ? "+" : ""}{tx.amount}
                    </td>
                  </tr>
                ))}
                {ledger.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-12 text-sm text-zinc-500">No transactions yet. Complete a task to earn credits.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="departments" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {deptWallets.map((w) => {
              const used = w.lifetime_spent || 0;
              const total = (w.balance || 0) + (w.reserved || 0) + used;
              const usagePct = total ? Math.round((used / total) * 100) : 0;
              return (
                <div key={w.wallet_id} className="border border-border p-4 bg-background" data-testid={`dept-wallet-${w.department_id}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{depts[w.department_id] || w.department_id}</div>
                  <div className="text-2xl font-mono font-bold mt-1 text-[#E1252A]">{w.balance}</div>
                  <div className="text-xs text-zinc-500">credits available</div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div><div className="text-zinc-500">Reserved</div><div className="font-mono">{w.reserved}</div></div>
                    <div><div className="text-zinc-500">Spent</div><div className="font-mono">{used}</div></div>
                    <div><div className="text-zinc-500">Allocation</div><div className="font-mono">{w.monthly_allocation}</div></div>
                  </div>
                  <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 mt-3 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#E1252A]" style={{ width: `${Math.min(100, usagePct)}%` }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{usagePct}% utilized</div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BigStat({ label, value, accent, icon: Icon, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <Icon size={14} className="text-zinc-400" />
      </div>
      <div className="text-2xl sm:text-3xl mt-2 font-bold font-mono tracking-tight" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
